/**
 * @name Report
 * @description Componente común para mostrar reportes
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';

import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/index.css';

import { LoadingWrapper, NoRegistersFound } from '@components';
import { Flex, MUI, Unicons, Typography } from '@styles';
import { Helpers } from '@helpers';

import _ from 'lodash';

// Components
import MenuColumns from './components/menu-columns';
import MenuExport from './components/menu-export';

class Report extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hiddenColumns: [],
      visibleColumns: [],
      // orderColumns: [],
      rows: [],
      searchText: '',
      gridRef: null,
      showHeader: props.showHeader !== false,
      showSearchBox: props.showSearchBox ? props.showSearchBox : false,
    };
    this.searchTextRef = React.createRef(this.state.searchText);
    this.searchTextRef.current = this.state.searchText;
  }

  componentDidMount() {
    this.setColumns();
    this.setRows();
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(this.props.columns, prevProps.columns)) this.setColumns();
    if (!_.isEqual(this.props.rows, prevProps.rows)) this.setRows();
  }

  setGridRef = (ref) => {
    this.setState({ gridRef: ref });
  }

  setColumns = () => {
    const { columns } = this.props;
    let visibleColumns = columns.filter((column) => column.visible);
    let hiddenColumns = visibleColumns.filter((column) => !column.hidden);
    this.setState({ visibleColumns, hiddenColumns });
  }

  setRows = () => {
    const { rows } = this.props;
    this.setState({ rows });
  }

  // *************************************
  // VISIBLE COLUMNS
  // *************************************

  hideColumnHandler = (item) => {
    const { visibleColumns } = this.state;
    visibleColumns[visibleColumns.findIndex((column) => column.name === item.name)].hidden = !item.hidden;
    let hiddenColumns = visibleColumns.filter((column) => !column.hidden);

    this.setState({ hiddenColumns });
  }

  // *************************************
  // SEARCH BOX
  // *************************************

  onSearchChange = ({ target: { value } }) => {
    const visibleColumns = this.state.gridRef.current.columns;
    const lowerSearchText = value && Helpers.removeAccents(value).toLowerCase();
    const newData = this.props.rows.filter((p) => {
      return visibleColumns.reduce((acc, col) => {
        const v = Helpers.removeAccents(`${p[col.name]}`).toLowerCase();
        return acc || v.indexOf(lowerSearchText) !== -1;
      }, false);
    });
    this.setState({ searchText: value, rows: newData });
  };

  render() {
    return (
      <LoadingWrapper area="loading-report">
        {(this.state.showHeader) && (
          <Styled.ReportHeader>
            <Flex.Row flex="1">
              {/* Registros totales */}
              <Typography.Text style={{ marginRight: '16px' }}>
                {`${i18n.t('GenericMessages.total')}: `}
                <Typography.Medium>{`${this.props.rowsLength} ${i18n.t('GenericMessages.registers').toLowerCase()}`}</Typography.Medium>
              </Typography.Text>
              {/* Registros filtrados */}
              <Typography.Text style={{ marginRight: '16px' }}>
                {`${i18n.t('GenericMessages.filtered')}: `}
                <Typography.Medium>{`${this.state.rows.length} ${i18n.t('GenericMessages.registers').toLowerCase()}`}</Typography.Medium>
              </Typography.Text>
            </Flex.Row>
            {/* Buscador */}
            {(this.state.showSearchBox) && (
              <MUI.Box padding="0 8px" marginRight="8px">
                <Styled.SearchTextField
                  type="text"
                  placeholder={`${i18n.t('GenericMessages.search')}...`}
                  value={this.state.searchText}
                  onChange={this.onSearchChange}
                  InputProps={{ startAdornment: <Unicons.Search size="20" style={{ minWidth: '20px', minHeight: '20px' }} /> }}
                />
              </MUI.Box>
            )}
            {/* Menú de exportar */}
            {(this.props.exportToCSV || this.props.exportAllToExcel || this.props.exportFiltersToExcel) && (
              <MUI.Box marginRight="8px">
                <MenuExport
                  columns={this.state.visibleColumns}
                  rows={this.state.rows}
                  exportToCSV={this.props.exportToCSV}
                  exportAllToExcel={this.props.exportAllToExcel}
                  exportFiltersToExcel={this.props.exportFiltersToExcel}
                  exportFileName={this.props.exportFileName}
                  onExportAllToExcel={this.props.onExportAllToExcel}
                  onExportFiltersToExcel={this.props.onExportFiltersToExcel}
                />
              </MUI.Box>
            )}
            {/* Menú de columnas */}
            <MUI.Box>
              <MenuColumns
                columns={this.state.visibleColumns}
                onHideColumn={this.hideColumnHandler}
              />
            </MUI.Box>
          </Styled.ReportHeader>
        )}
        <Styled.ReportGrid>
          <ReactDataGrid
            idProperty="id"
            handle={this.setGridRef}
            columns={this.state.hiddenColumns}
            dataSource={this.state.rows}
            showCellBorders={'vertical'}
            showColumnMenuGroupOptions={false}
            showColumnMenuLockOptions={false}
            showColumnMenuSortOptions={false}
            showColumnMenuTool={false}
            shareSpaceOnResize={false}
            resizable={true}
            reorderColumns={true}
            scrollProps={{
              autoHide: false,
              alwaysShowTrack: true,
              scrollThumbWidth: 8,
              scrollThumbOverWidth: 8
            }}
            emptyText={<NoRegistersFound />}
            style={this.props.styles}
          />
        </Styled.ReportGrid>
      </LoadingWrapper>
    );
  }
}

Report.propTypes = {
  // Colección de columnas a mostrar en el grid. 
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      visible: PropTypes.bool.isRequired,
      hidden: PropTypes.bool,
      enableHiding: PropTypes.bool,
      resizable: PropTypes.bool,
      sortable: PropTypes.bool,
      draggable: PropTypes.bool,
      minWidth: PropTypes.number,
    })
  ),
  // Registros mostrados actualmente en el grid.
  rows: PropTypes.array.isRequired,
  // Número de registros mostrados actualmente en el grid.
  rowsLength: PropTypes.number.isRequired,
  // Boolean que muestra el header del componente.
  showHeader: PropTypes.bool,
  // Boolean que muestra el input de busqueda.
  showSearchBox: PropTypes.bool,
  // Boolean que muestra la opción de exportar a csv.
  exportToCSV: PropTypes.bool,
  // Boolean que muestra la opción de exportar a todos los registros a Excel.
  exportAllToExcel: PropTypes.bool,
  // Boolean que muestra la opción de exportar a los registros filtrados a Excel.
  exportFiltersToExcel: PropTypes.bool,
  // Nombre que se le da al archivo cuando se exportan los registros.
  exportFileName: PropTypes.string,
  // Función para exportar a todos los registros a Excel.
  onExportAllToExcel: PropTypes.func,
  // Función para exportar a los registros filtrados a Excel.
  onExportFiltersToExcel: PropTypes.func,
  // Styles
  styles: PropTypes.object
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.ReportGrid = styled.div`
  .InovuaReactDataGrid--theme-default-light {
    color: ${(props) => props.theme.colors.greyDark};
    font-size: ${(props) => props.theme.typography.bodyText};
    font-variant: tabular-nums;
    font-weight: normal;
    line-height: 1.3;
    letter-spacing: 0.0175em;
  }

  .InovuaReactDataGrid--theme-default-light .InovuaReactDataGrid__column-header__content {
    font-family: 'Bryant Pro Medium';
    font-weight: normal;
    line-height: 1.3;
    letter-spacing: 0.0175em;
  }
`;

Styled.ReportHeader = styled(Flex.Row)`
  padding: 8px 0px;
`;

Styled.SearchTextField = styled(MUI.TextFieldIcon)`
  &.MuiFormControl-root {
    min-height: 40px;
  }
`;

export default Report;